import { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPhone, FiSave } from "react-icons/fi";
import { updateContact, updatePhoneCall } from "../../../services/contact_service";
import useLookupStore from "../../../states/useLookupStore";
import { Input, Modal } from "@iq/models";
import "../Modal.css";
import { PHONE_CALL_OUTCOMES, getPhoneCallOutcomeLabel, normalizePhoneCallOutcome } from "../../../models/phoneCallOutcomes";

const DIRECTION_OPTIONS = [
	{ value: "outbound", label: "Outbound" },
	{ value: "inbound", label: "Inbound" },
];

const CALL_TYPE_OPTIONS = [
	{ value: "call", label: "Call" },
	{ value: "voicemail", label: "Voicemail" },
	{ value: "power", label: "Power Dialer" },
];

function splitName(value) {
	const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) {
		return { firstName: "", lastName: "" };
	}
	if (parts.length === 1) {
		return { firstName: parts[0], lastName: "" };
	}
	return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function buildFormState(phoneCall) {
	return {
		contactName: phoneCall?.contact_name || phoneCall?.manual_contact_name || "",
		contactEmail: phoneCall?.contact_email || "",
		contactPhone: phoneCall?.contact_phone || "",
		contactAccount: phoneCall?.contact_account || "",
		contactAccountId: phoneCall?.account_id || "",
		dealTitle: phoneCall?.deal_title || "",
		direction: phoneCall?.direction || "outbound",
		callType: phoneCall?.call_type || "call",
		outcome: normalizePhoneCallOutcome(phoneCall?.outcome),
		notes: phoneCall?.notes || "",
		durationSeconds: String(phoneCall?.duration_seconds || 0),
		callAt: phoneCall?.call_at ? String(phoneCall.call_at).slice(0, 16) : "",
	};
}

function PhoneLoggerModal({ isOpen, phoneCall, onClose, onSaved }) {
	const contacts = useLookupStore((state) => state.contacts);
	const accounts = useLookupStore((state) => state.accounts);
	const hydrateLookups = useLookupStore((state) => state.hydrateLookups);
	const setLookups = useLookupStore((state) => state.setLookups);
	const [isEditing, setIsEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [form, setForm] = useState(buildFormState(phoneCall));

	const lookupAccounts = useMemo(() => (Array.isArray(accounts) ? accounts : []), [accounts]);
	const lookupContacts = useMemo(() => (Array.isArray(contacts) ? contacts : []), [contacts]);

	const directionLabel = DIRECTION_OPTIONS.find((option) => option.value === form.direction)?.label || "";
	const callTypeLabel = CALL_TYPE_OPTIONS.find((option) => option.value === form.callType)?.label || "";
	const outcomeLabel = form.outcome ? getPhoneCallOutcomeLabel(form.outcome) : "";

	function updateField(field, value) {
		setForm((prev) => ({ ...prev, [field]: value }));
	}

	useEffect(() => {
		if (!isOpen || !phoneCall) {
			return;
		}

		hydrateLookups();
		setForm(buildFormState(phoneCall));
		setIsEditing(false);
		setSaving(false);
		setError("");
	}, [hydrateLookups, phoneCall, isOpen]);

	if (!isOpen || !phoneCall) {
		return null;
	}

	function handleChange(event) {
		const { name, value } = event.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	}

	function handleCancelEdit() {
		setForm(buildFormState(phoneCall));
		setIsEditing(false);
		setError("");
	}

	const canEditLinkedContactName = Boolean(phoneCall.contact_id);

	async function handleSave() {
		if (!form.outcome) {
			setError("Select a structured call outcome before saving.");
			return;
		}
		setError("");
		setSaving(true);

		try {
			const updatedCall = await updatePhoneCall(phoneCall.id, {
				contact_id: phoneCall.contact_id || undefined,
				manual_contact_name: phoneCall.contact_id ? "" : form.contactName,
				deal_title: form.dealTitle,
				direction: form.direction,
				call_type: form.callType,
				outcome: form.outcome,
				notes: form.notes,
				duration_seconds: Number.parseInt(form.durationSeconds || "0", 10) || 0,
				call_at: form.callAt ? new Date(form.callAt).toISOString() : undefined,
			});

			let updatedContact = null;
			const contactFieldsChanged = Boolean(phoneCall.contact_id) && (
				String(form.contactName || "").trim() !== String(phoneCall.contact_name || "").trim() ||
				String(form.contactEmail || "").trim() !== String(phoneCall.contact_email || "").trim() ||
				String(form.contactPhone || "").trim() !== String(phoneCall.contact_phone || "").trim() ||
				String(form.contactAccountId || "") !== String(phoneCall.account_id || "")
			);

			if (contactFieldsChanged) {
				try {
					updatedContact = await updateContact(phoneCall.contact_id, {
						email: form.contactEmail,
						firstName: splitName(form.contactName).firstName,
						lastName: splitName(form.contactName).lastName,
						jobTitle: "",
						phone: form.contactPhone,
						mobile: form.contactPhone,
						accountId: form.contactAccountId,
						company: form.contactAccount,
						account: form.contactAccount,
					});
				} catch (contactErr) {
					console.warn("Contact update failed while phone call was saved", contactErr);
				}
			}

			if (updatedContact) {
				const nextContacts = lookupContacts.some((contact) => Number(contact.id) === Number(updatedContact.id))
					? lookupContacts.map((contact) => (Number(contact.id) === Number(updatedContact.id) ? updatedContact : contact))
					: [updatedContact, ...lookupContacts];
				setLookups({ contacts: nextContacts });
			}

			onSaved?.(updatedCall);
			onClose?.();
		} catch (err) {
			setError(err?.message || "Failed to save phone call");
		} finally {
			setSaving(false);
		}
	}

	const actionButtons = isEditing
		? [
				{
					key: "cancel-edit",
					text: "Cancel",
					color: "dark",
					onClick: handleCancelEdit,
					disabled: saving || !form.outcome,
				},
				{
					key: "save-phone-call",
					text: saving ? "Saving..." : "Save Changes",
					icon: <FiSave />,
					color: "accent",
					onClick: handleSave,
					disabled: saving,
				},
			]
		: [
				{
					key: "edit-phone-call",
					text: "Edit Call",
					icon: <FiEdit2 />,
					color: "accent",
					onClick: () => setIsEditing(true),
					disabled: saving,
				},
			];

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Phone Call"
			icon={<FiPhone />}
			actionButtons={actionButtons}
			errorMessage={error}
			shrink={true}
		>
			<div className="modal-form-container">
				<div className="form-fields-container">
					<div className="modal-form-field wide-form-field">
						<label>Contact Name</label>
						{isEditing ? (
							<Input
								name="contactName"
								value={form.contactName}
								onChange={handleChange}
								disabled={saving || canEditLinkedContactName}
								colorScheme="light"
							/>
						) : (
							<div className="form-static-value">{form.contactName || "-"}</div>
						)}
						{isEditing && canEditLinkedContactName ? (
							<span className="modal-form-field-note">Edit the linked contact name from the contact record.</span>
						) : null}
					</div>

					<div className="modal-form-field">
						<label>Contact Email</label>
						{isEditing ? (
							<Input name="contactEmail" value={form.contactEmail} onChange={handleChange} disabled={saving} colorScheme="light" />
						) : (
							<div className="form-static-value">{form.contactEmail || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Phone Number</label>
						{isEditing ? (
							<Input name="contactPhone" value={form.contactPhone} onChange={handleChange} disabled={saving} colorScheme="light" />
						) : (
							<div className="form-static-value">{form.contactPhone || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Account</label>
						{isEditing ? (
							<Input
								type="dropdown"
								name="contactAccountId"
								placeholder="No account"
								value={form.contactAccountId}
								disabled={saving}
								colorScheme="light"
								renderInPortal={true}
								options={lookupAccounts.map((account) => ({ value: account.id, label: account.name }))}
								onOptionSelect={(option) => {
									updateField("contactAccountId", option.value);
									updateField("contactAccount", option.label);
								}}
							/>
						) : (
							<div className="form-static-value">{form.contactAccount || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Deal Title</label>
						{isEditing ? (
							<Input name="dealTitle" value={form.dealTitle} onChange={handleChange} disabled={saving} colorScheme="light" />
						) : (
							<div className="form-static-value">{form.dealTitle || "No deal linked"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Direction</label>
						{isEditing ? (
							<Input
								type="dropdown"
								name="direction"
								value={directionLabel}
								disabled={saving}
								colorScheme="light"
								renderInPortal={true}
								options={DIRECTION_OPTIONS}
								onOptionSelect={(option) => updateField("direction", option.value)}
							/>
						) : (
							<div className="form-static-value">{form.direction || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Type</label>
						{isEditing ? (
							<Input
								type="dropdown"
								name="callType"
								value={callTypeLabel}
								disabled={saving}
								colorScheme="light"
								renderInPortal={true}
								options={CALL_TYPE_OPTIONS}
								onOptionSelect={(option) => updateField("callType", option.value)}
							/>
						) : (
							<div className="form-static-value">{form.callType || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Duration Seconds</label>
						{isEditing ? (
							<Input name="durationSeconds" type="number" min="0" value={form.durationSeconds} onChange={handleChange} disabled={saving} colorScheme="light" />
						) : (
							<div className="form-static-value">{form.durationSeconds || "0"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Call Time</label>
						{isEditing ? (
							<Input name="callAt" type="datetime-local" value={form.callAt} onChange={handleChange} disabled={saving} fieldClassName="modal-form-field-datetime" colorScheme="light" />
						) : (
							<div className="form-static-value">{form.callAt || "-"}</div>
						)}
					</div>

					<div className="modal-form-field">
						<label>Outcome</label>
						{isEditing ? (
							<Input
								type="dropdown"
								name="outcome"
								value={outcomeLabel}
								disabled={saving}
								colorScheme="light"
								renderInPortal={true}
								options={PHONE_CALL_OUTCOMES}
								onOptionSelect={(option) => updateField("outcome", option.value)}
							/>
						) : (
							<div className="form-static-value">{getPhoneCallOutcomeLabel(phoneCall.outcome)}</div>
						)}
					</div>

					<div className="modal-form-field wide-form-field">
						<label>Notes</label>
						{isEditing ? (
							<Input type="textarea" name="notes" rows={5} value={form.notes} onChange={handleChange} disabled={saving} colorScheme="light" />
						) : (
							<div className="form-static-value">{form.notes || "No notes"}</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
}

export default PhoneLoggerModal;
